import { FormikErrors } from 'formik';
import { get } from 'lodash';
import { ParsedCard, ParsedForm } from 'store/declaration-form-errors/ParsedForm';
import { ICS2EnsDeclaration } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { parseFieldErrorsForCard } from 'utils/validationParserUtils';
import {
    EMPTY_LEVEL_INDEX,
    emptyLevelMessage,
    parseAdditionalSupplyChainActorCard,
    parseContainerCard,
    parseCustomsOfficeCard,
    parseDangerousGoodsCard,
    parseEmptyLevels,
    parseGoodsInformationCard,
    parseHouseConsignmentDetailsCard,
    parseHouseConsignmentPlacesCard,
    parseHouseConsignments,
    parseHouseTransportDocumentCard,
    parseMasterPlacesCard,
    parseMasterTransportDetailsCard,
    parseMasterTransportDocumentCard,
    parsePackagesCard,
    parsePartiesCard,
    parsePassiveBorderTransportMeansCard,
    parseSharedCards,
    parseSupportingDocumentsCard,
} from './ics2EnsValidationCards';

export const parseIcs2F50GoodsItemCards = (validation: any): ParsedCard[] => {
    const errors: ParsedCard[] = [];
    if (!validation) return errors;

    parseGoodsInformationCard(validation, errors);
    parseDangerousGoodsCard(validation, errors);
    parsePackagesCard(validation, errors);

    parsePassiveBorderTransportMeansCard(validation, errors);

    parseSharedCards(validation, errors);

    return errors;
};

export const parseIcs2F50HouseConsignmentCards = (validation: any): ParsedCard[] => {
    const errors: ParsedCard[] = [];
    if (!validation) return errors;

    parseHouseConsignmentDetailsCard(validation, errors);

    parsePartiesCard(
        validation,
        [
            { path: 'goodsShipment.seller', label: 'Seller' },
            { path: 'goodsShipment.buyer', label: 'Buyer' },
        ],
        errors
    );

    parseFieldErrorsForCard(
        'Transport Details',
        [
            { 'transportCharges.methodOfPayment': 'Transport Method of Payment' },
            { 'countriesOfRoutingOfConsignment.0.country': 'Country of Routing of Consignment' },
            { countriesOfRoutingOfConsignment: 'Countries of Routing of Consignment' },
        ],
        errors,
        validation
    );

    parsePassiveBorderTransportMeansCard(validation, errors);

    parseHouseConsignmentPlacesCard(validation, errors);
    parseHouseTransportDocumentCard(validation, errors);
    parseContainerCard(validation, errors);
    parseSharedCards(validation, errors);

    return errors;
};

const HOUSE_CONSIGNMENTS_PATH = 'consignmentMasterLevel.consignmentHouseLevel';

const ics2EnsF50ValidationErrorsParser = (validation: FormikErrors<ICS2EnsDeclaration>): ParsedForm => {
    const masterDetails: ParsedCard[] = [];

    parseFieldErrorsForCard(
        'Master Details',
        [
            { lrn: 'LRN' },
            { 'documentIssueDate.dateTime': 'Document Issue Date' },
            { specificCircumstanceIndicator: 'Specific Circumstance Indicator' },
            { 'consignmentMasterLevel.referenceNumberUCR.referenceNumberUCR': 'Reference Number/UCR' },
        ],
        masterDetails,
        validation as any
    );

    parsePartiesCard(
        validation,
        [
            { path: 'consignmentMasterLevel.consignor', label: 'Consignor' },
            { path: 'consignmentMasterLevel.consignee', label: 'Consignee' },
            { path: 'declarant', label: 'Declarant' },
            { path: 'representative', label: 'Representative' },
            { path: 'consignmentMasterLevel.carrier', label: 'Carrier' },
        ],
        masterDetails
    );

    parseMasterTransportDetailsCard(validation, masterDetails);
    parseMasterPlacesCard(validation, masterDetails);
    parseCustomsOfficeCard(validation, masterDetails);
    parseMasterTransportDocumentCard(validation, masterDetails);

    parseSupportingDocumentsCard(validation, masterDetails, 'consignmentMasterLevel');
    parseAdditionalSupplyChainActorCard(validation, masterDetails, 'consignmentMasterLevel');

    parseEmptyLevels(validation, masterDetails, [{ path: HOUSE_CONSIGNMENTS_PATH, name: 'House Consignments' }]);

    const houseConsignments = parseHouseConsignments(
        get(validation, HOUSE_CONSIGNMENTS_PATH),
        parseIcs2F50HouseConsignmentCards,
        parseIcs2F50GoodsItemCards
    );

    // An empty level is marked as an entry numbered 0, which matches no consignment in the list but
    // does mark the tab of the level that has nothing in it.
    if (emptyLevelMessage(validation, HOUSE_CONSIGNMENTS_PATH)) {
        houseConsignments.push({ index: EMPTY_LEVEL_INDEX, errors: [], items: [] });
    }

    return {
        masterDetails,
        // ICS2 keeps its items apart: the single item list is what the other declaration types use.
        items: [],
        // F50 has no master item level.
        masterItems: [],
        houseConsignments,
    };
};

export default ics2EnsF50ValidationErrorsParser;
